import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TeamLogo from "../TeamLogo";
import { useDarkMode } from "../../hooks/useDarkmode";
import {
    BracketMatch,
    BracketSection,
    QualifierBracket as BracketData,
} from "../../services/TournamentService";
import styles from "./QualifierBracket.module.css";

/**
 * Bracket geometry, measured off the reference implementation. Mirrored in the CSS
 * (.roundHeader height, .match padding) — change both together.
 */
const METRICS = {
    columnWidth: 186,
    gutter: 28,
    cardHeight: 78,
    /** Vertical distance between adjacent cards in the densest round. */
    pitch: 92,
    /** Corner radius on the connector elbows. */
    bendRadius: 10,
};

/**
 * An orthogonal connector from one card's right edge to another's left edge, with rounded
 * corners.
 *
 * The vertical leg sits at <paramref name="bend"/>, which callers place in the gutter directly
 * before the destination column rather than at the midpoint of the whole span. That distinction
 * matters for any connector that skips a round — routine in a losers bracket, where a team can
 * sit out while the other half plays down. Bending at the midpoint of a multi-column span drew
 * the vertical leg straight through the cards in between; bending in the adjacent gutter keeps
 * it in empty space.
 */
function elbow(x1: number, y1: number, x2: number, y2: number, bend: number): string {
    if (Math.abs(y2 - y1) < 0.5) return `M ${x1} ${y1} H ${x2}`;

    const down = y2 > y1;
    const radius = Math.min(
        METRICS.bendRadius,
        Math.abs(y2 - y1) / 2,
        Math.abs(bend - x1),
        Math.abs(x2 - bend),
    );
    const step = down ? radius : -radius;

    return [
        `M ${x1} ${y1}`,
        `H ${bend - radius}`,
        `A ${radius} ${radius} 0 0 ${down ? 1 : 0} ${bend} ${y1 + step}`,
        `V ${y2 - step}`,
        `A ${radius} ${radius} 0 0 ${down ? 0 : 1} ${bend + radius} ${y2}`,
        `H ${x2}`,
    ].join(' ');
}

interface Props {
    bracket: BracketData;
    /** Warnings are derivation diagnostics — only useful to admins. */
    showWarnings?: boolean;
}

interface PositionedMatch {
    match: BracketMatch;
    roundIndex: number;
    x: number;
    y: number;
}

interface SectionLayout {
    nodes: PositionedMatch[];
    connectors: string[];
    width: number;
    height: number;
}

/**
 * Lay one section (a winners bracket, a losers bracket, or a whole single-elimination tree)
 * out as a tree.
 *
 * Each card sits level with the midpoint of the cards feeding it. Feeders are found by team
 * identity rather than by pairing up positions, because round sizes are irregular: a 14-team
 * qualifier plays 6 matches then 4, so there is no clean two-into-one mapping to rely on. The
 * same reason makes this work for a losers bracket, whose shape depends on when teams drop out
 * of the winners bracket.
 */
function layoutSection(section: BracketSection): SectionLayout {
    const rounds = section.rounds;
    if (rounds.length === 0 || rounds.every((r) => r.matches.length === 0)) {
        return { nodes: [], connectors: [], width: 0, height: 0 };
    }

    const ys: number[][] = [];
    ys[0] = rounds[0].matches.map((_, i) => i * METRICS.pitch);

    for (let r = 1; r < rounds.length; r++) {
        const previous = rounds[r - 1].matches;
        const previousYs = ys[r - 1];

        const placed = rounds[r].matches.map((match) => {
            const feeders: number[] = [];
            previous.forEach((candidate, index) => {
                const winnerId = candidate.winnerId;
                if (!winnerId) return;
                if (match.team1?.teamId === winnerId || match.team2?.teamId === winnerId) {
                    feeders.push(previousYs[index]);
                }
            });

            if (feeders.length === 0) return null;
            return feeders.reduce((a, b) => a + b, 0) / feeders.length;
        });

        // Matches with no traceable feeder — both teams arrived on byes, a team has just dropped
        // in from the winners bracket, or the feed is missing a match — go below everything
        // already placed so nothing overlaps.
        let next = Math.max(0, ...placed.filter((y): y is number => y !== null)) + METRICS.pitch;
        ys[r] = placed.map((y) => {
            if (y !== null) return y;
            const assigned = next;
            next += METRICS.pitch;
            return assigned;
        });

        // Two feeders can resolve to the same midpoint; nudge apart in visual order.
        const order = ys[r].map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
        order.forEach((entry, position) => {
            if (position === 0) return;
            const above = ys[r][order[position - 1].i];
            if (entry.y - above < METRICS.pitch) {
                ys[r][entry.i] = above + METRICS.pitch;
            }
        });
    }

    const height = Math.max(...ys.flat().map((y) => y + METRICS.cardHeight), METRICS.cardHeight);

    const nodes: PositionedMatch[] = [];
    rounds.forEach((round, roundIndex) => {
        round.matches.forEach((match, i) => {
            nodes.push({
                match,
                roundIndex,
                x: roundIndex * (METRICS.columnWidth + METRICS.gutter),
                y: ys[roundIndex][i],
            });
        });
    });

    // A match connects forward to wherever its winner next appears in this section.
    const connectors: string[] = [];
    nodes.forEach((node) => {
        const winnerId = node.match.winnerId;
        if (!winnerId) return;

        const next = nodes.find(
            (candidate) =>
                candidate.roundIndex > node.roundIndex &&
                (candidate.match.team1?.teamId === winnerId || candidate.match.team2?.teamId === winnerId)
        );
        if (!next) return;

        const x1 = node.x + METRICS.columnWidth;
        const y1 = node.y + METRICS.cardHeight / 2;
        const x2 = next.x;
        const y2 = next.y + METRICS.cardHeight / 2;

        // Turn in the gutter beside the destination, never across the columns in between.
        connectors.push(elbow(x1, y1, x2, y2, x2 - METRICS.gutter / 2));
    });

    return {
        nodes,
        connectors,
        width: rounds.length * METRICS.columnWidth + (rounds.length - 1) * METRICS.gutter,
        height,
    };
}

/**
 * Renders one qualifier. Single elimination arrives as a single section; double elimination as
 * a winners bracket, a losers bracket and optionally a grand final, each laid out separately
 * and stacked, which is how the reference site presents them.
 */
const QualifierBracket = ({ bracket, showWarnings = false }: Props) => {
    const { t } = useTranslation(['pages', 'common']);
    const isDark = useDarkMode();
    const [tab, setTab] = useState<'bracket' | 'seeding'>('bracket');

    const hasSeeding = bracket.seedingMatches.length > 0;
    const layouts = useMemo(
        () => bracket.sections.map((section) => ({ section, layout: layoutSection(section) })),
        [bracket.sections]
    );

    const renderSide = (match: BracketMatch, which: 1 | 2) => {
        const team = which === 1 ? match.team1 : match.team2;

        // A best-of-one is far more readable as its round score (13-6) than as 1-0. The backend
        // fills these in from our parsed matches; unparsed games fall back to maps won.
        const hasRoundScore = match.roundScore1 !== null && match.roundScore2 !== null;
        const score = hasRoundScore
            ? (which === 1 ? match.roundScore1 : match.roundScore2)!
            : (which === 1 ? match.score1 : match.score2);

        if (!team) {
            return (
                <div className={`${styles.side} ${styles.tbd}`}>
                    <span className={styles.logo} />
                    <span className={styles.name}>{t('pages:standings.bracket.tbd')}</span>
                </div>
            );
        }

        const decided = match.isComplete && !!match.winnerId;
        const isWinner = decided && match.winnerId === team.teamId;
        const state = !decided ? styles.pending : isWinner ? styles.winner : styles.loser;

        // Forfeit marker on the losing side, for both flavours: a result the feed recorded
        // backwards, and a match the feed left out entirely and we deduced.
        const showForfeit = (match.winnerDisputed || match.isReconstructed) && !isWinner;
        const forfeitHint = match.isReconstructed
            ? t('pages:standings.bracket.reconstructedHint')
            : t('pages:standings.bracket.forfeitHint');

        return (
            <div className={`${styles.side} ${state}`}>
                <TeamLogo logo={team.logo} name={team.name} className={styles.logo} />
                <span className={styles.name}>{team.name || t('pages:standings.bracket.tbd')}</span>
                {showForfeit && (
                    <span className={styles.ff} title={forfeitHint}>
                        {t('pages:standings.bracket.forfeit')}
                    </span>
                )}
                <span className={styles.score}>{decided || score > 0 ? score : '-'}</span>
            </div>
        );
    };

    const renderMatch = (match: BracketMatch, style?: React.CSSProperties) => {
        const body = (
            <>
                {renderSide(match, 1)}
                {renderSide(match, 2)}
            </>
        );

        const key = match.matchId || `${match.team1?.teamId}-${match.team2?.teamId}`;

        // Only completed matches have anything to show on a match page.
        if (match.isComplete && match.matchId) {
            return (
                <Link to={`/match/${match.matchId}`} className={styles.match} style={style} key={key}>
                    {body}
                </Link>
            );
        }

        return (
            <div className={styles.match} style={style} key={key}>
                {body}
            </div>
        );
    };

    const roundLabel = (sectionKey: string, roundNumber: number) => {
        const prefix =
            sectionKey === 'winners' ? t('pages:standings.bracket.wbShort')
            : sectionKey === 'losers' ? t('pages:standings.bracket.lbShort')
            : null;

        const round = t('pages:standings.bracket.round', { number: roundNumber });
        return prefix ? `${prefix} ${round}` : round;
    };

    const renderSection = (section: BracketSection, layout: SectionLayout) => {
        if (layout.nodes.length === 0) return null;

        // A single-elimination bracket is the whole thing, so it needs no heading of its own.
        const heading =
            section.key === 'main' ? null : t(`pages:standings.bracket.section.${section.key}`);

        return (
            <div key={section.key} className={styles.section}>
                {heading && <div className={styles.sectionTitle}>{heading}</div>}
                <div className={styles.scroller}>
                    <div style={{ width: layout.width }}>
                        <div className={styles.roundHeaders} style={{ gap: METRICS.gutter }}>
                            {section.rounds.map((round) => (
                                <div
                                    key={round.roundNumber}
                                    className={styles.roundHeader}
                                    style={{ width: METRICS.columnWidth }}
                                >
                                    {roundLabel(section.key, round.roundNumber)}
                                </div>
                            ))}
                        </div>

                        <div className={styles.grid} style={{ height: layout.height }}>
                            <svg
                                className={styles.connectors}
                                width={layout.width}
                                height={layout.height}
                                aria-hidden="true"
                            >
                                {layout.connectors.map((d, i) => (
                                    <path
                                        key={i}
                                        d={d}
                                        fill="none"
                                        stroke={isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)'}
                                        strokeWidth={1.5}
                                        strokeLinecap="round"
                                    />
                                ))}
                            </svg>

                            {layout.nodes.map((node) => (
                                <div
                                    key={node.match.matchId || `${node.roundIndex}-${node.y}`}
                                    className={styles.node}
                                    style={{ left: node.x, top: node.y, width: METRICS.columnWidth }}
                                >
                                    {renderMatch(node.match, { height: METRICS.cardHeight })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const anyMatches = layouts.some(({ layout }) => layout.nodes.length > 0);

    return (
        <div className={`${styles.wrapper} ${isDark ? '' : styles.light}`}>
            <div className={styles.header}>
                <h3 className={styles.title}>{bracket.displayName}</h3>
                <span className={styles.meta}>
                    {t('pages:standings.bracket.teamCount', { count: bracket.teamCount })}
                    {bracket.byeCount > 0 &&
                        ` · ${t('pages:standings.bracket.byeCount', { count: bracket.byeCount })}`}
                    {bracket.format === 'double-elimination' &&
                        ` · ${t('pages:standings.bracket.doubleElimination')}`}
                </span>
            </div>

            {hasSeeding && (
                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={`${styles.tab} ${tab === 'bracket' ? styles.active : ''}`}
                        onClick={() => setTab('bracket')}
                    >
                        {t('pages:standings.bracket.playoffs')}
                    </button>
                    <button
                        type="button"
                        className={`${styles.tab} ${tab === 'seeding' ? styles.active : ''}`}
                        onClick={() => setTab('seeding')}
                    >
                        {t('pages:standings.bracket.seeding')}
                    </button>
                </div>
            )}

            {tab === 'bracket' ? (
                anyMatches ? (
                    layouts.map(({ section, layout }) => renderSection(section, layout))
                ) : (
                    <div className={styles.empty}>{t('pages:standings.bracket.noMatches')}</div>
                )
            ) : (
                <div className={styles.seedingList}>
                    {bracket.seedingMatches.map((match) => renderMatch(match))}
                </div>
            )}

            {showWarnings && bracket.warnings.length > 0 && (
                <div className={styles.warnings}>
                    <ul>
                        {bracket.warnings.map((warning, i) => (
                            <li key={i}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default QualifierBracket;
